import React from "react";

const CustomTimePicker = ({
  value,
  onChange,
  placeholder = "Select Duration",
  className = "",
  disabled = false,
  error = false,
}) => {
  // Parse existing value
  const [hours, minutes, seconds] = value ? value.split(":") : ["", "", ""];

  const handleHourChange = (e) => {
    const newHours = e.target.value;
    const currentMinutes = minutes || "00";
    const currentSeconds = seconds || "00";
    const newTime = newHours
      ? `${newHours.padStart(2, "0")}:${currentMinutes.padStart(
          2,
          "0"
        )}:${currentSeconds.padStart(2, "0")}`
      : "";
    if (onChange) {
      onChange(newTime);
    }
  };

  const handleMinuteChange = (e) => {
    const newMinutes = e.target.value;
    const currentHours = hours || "00";
    const currentSeconds = seconds || "00";
    const newTime =
      newMinutes && currentHours
        ? `${currentHours.padStart(2, "0")}:${newMinutes.padStart(
            2,
            "0"
          )}:${currentSeconds.padStart(2, "0")}`
        : "";
    if (onChange) {
      onChange(newTime);
    }
  };

  const handleSecondChange = (e) => {
    const newSeconds = e.target.value;
    const currentHours = hours || "00";
    const currentMinutes = minutes || "00";
    const newTime =
      newSeconds && currentHours && currentMinutes
        ? `${currentHours.padStart(2, "0")}:${currentMinutes.padStart(
            2,
            "0"
          )}:${newSeconds.padStart(2, "0")}`
        : "";
    if (onChange) {
      onChange(newTime);
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange("");
    }
  };

  // Generate options
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i.toString().padStart(2, "0"),
    label: `${i.toString().padStart(2, "0")}`,
  }));

  const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
    value: i.toString().padStart(2, "0"),
    label: `${i.toString().padStart(2, "0")}`,
  }));

  const secondOptions = Array.from({ length: 60 }, (_, i) => ({
    value: i.toString().padStart(2, "0"),
    label: `${i.toString().padStart(2, "0")}`,
  }));

  return (
    <div
      className={`custom-time-picker ${className} ${
        disabled ? "disabled" : ""
      }`}
    >
      <style>
        {`
          .custom-time-picker {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 16px;
            background: transparent;
            min-height: 48px;
          }

          .custom-time-picker.disabled {
            opacity: 0.6;
            pointer-events: none;
          }

          .time-field {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            min-width: 50px;
          }

          .time-label {
            font-size: 10px;
            font-weight: 600;
            color: #6c757d;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin: 0;
            line-height: 1;
          }

          .time-select {
            width: 50px;
            height: 32px;
            border: none;
            background: transparent;
            text-align: center;
            font-size: 14px;
            font-weight: 500;
            color: #495057;
            outline: none;
            appearance: none;
            -webkit-appearance: none;
            -moz-appearance: none;
            cursor: pointer;
            padding: 4px 2px;
          }

          .time-select:focus {
            background: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
            border-radius: 4px;
          }

          .time-select:hover:not(:disabled) {
            background: rgba(0, 0, 0, 0.05);
            border-radius: 4px;
          }

          .time-select:disabled {
            color: #adb5bd;
            cursor: not-allowed;
          }

          .time-select option {
            background: white;
            color: #495057;
          }

          .time-separator {
            font-size: 18px;
            font-weight: 600;
            color: #6c757d;
            line-height: 1;
            user-select: none;
          }

          .clock-icon {
            color: #6c757d;
            flex-shrink: 0;
          }

          .time-display {
            font-size: 14px;
            font-weight: 600;
            color: #0d6efd;
            min-width: 70px;
            text-align: center;
            font-family: 'Courier New', monospace;
          }

          .clear-button {
            background: none;
            border: none;
            color: #6c757d;
            font-size: 16px;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
            flex-shrink: 0;
          }

          .clear-button:hover {
            background: #e9ecef;
            color: #dc3545;
          }
        `}
      </style>

      {/* Clock Icon */}
      <div className="clock-icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
          <path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" />
        </svg>
      </div>

      {/* Hours Field */}
      <div className="time-field">
        <label className="time-label">HH</label>
        <select
          className="time-select"
          value={hours || ""}
          onChange={handleHourChange}
          disabled={disabled}
        >
          <option value="" disabled>
            --
          </option>
          {hourOptions.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Separator */}
      <div className="time-separator">:</div>

      {/* Minutes Field */}
      <div className="time-field">
        <label className="time-label">MM</label>
        <select
          className="time-select"
          value={minutes || ""}
          onChange={handleMinuteChange}
          disabled={disabled || !hours}
        >
          <option value="" disabled>
            --
          </option>
          {minuteOptions.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Separator */}
      <div className="time-separator">:</div>

      {/* Seconds Field */}
      <div className="time-field">
        <label className="time-label">SS</label>
        <select
          className="time-select"
          value={seconds || ""}
          onChange={handleSecondChange}
          disabled={disabled || !hours || !minutes}
        >
          <option value="" disabled>
            --
          </option>
          {secondOptions.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Time Display - Always present for consistent UI */}


      {/* Clear Button */}
      {value && !disabled && (
        <button
          type="button"
          className="clear-button"
          onClick={handleClear}
          title="Clear time"
        >
          ×
        </button>
      )}
    </div>
  );
};

export default CustomTimePicker;
